import React, { useEffect, useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { FaCheck } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllTaskDetailsByloginId, getTaskDataProjectWiseData, getEmpTaskCountList } from '../../Redux/Task/Action';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getLeaveBalanceList } from "../../Redux/Leave/Action";
import { getAlerts, alertCompleted } from '../../Redux/Alert/Action';
import { getEmployeeData } from '../../Redux/NewHire/Action';
import Swal from "sweetalert2";
import { format } from 'date-fns';
import { dailyUpdateByLoginIdAsQuarter } from '../../Redux/DailyUpdate/Action';
import { empList } from '../../Redux/DailyUpdateApproval/Action';
import Select from 'react-select';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

function parseDDMMYYYY(dateStr) {
    if (typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;

    const [day, month, year] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

    return new Date(year, month - 1, day); // JS months are 0-indexed
}

function stripTime(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const getCurrentQuarterDetails = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based

    const quarter = Math.floor(month / 3) + 1;
    const startMonth = (quarter - 1) * 3;

    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const quarterLabel = `${monthNames[startMonth]}-${monthNames[startMonth + 2]}`;

    return {
        quarter: quarterLabel,
        startDate,
        endDate
    };
};


const AdminDash = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { allTaskDetails, projectWiseData, empTaskContList } = useSelector((store) => store.task);
    const { alerts } = useSelector((state) => state.alert);
    const { employeeData } = useSelector((store) => store.newHireData);
    const { dailyUpdatebyIdPerQuarter } = useSelector((store) => store.dailyUpdate);
    const { supportlists } = useSelector((store) => store.dailyUpdateApprover);

    const token = localStorage.getItem("token");
    const loginId = localStorage.getItem("loginId");
    const role = localStorage.getItem("role");
    const [quarterInfo, setQuarterInfo] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [statusCounts, setStatusCounts] = useState({});

    const getDailyUpdateData = (loginId) => {
        const data = getCurrentQuarterDetails();
        setQuarterInfo(data);
        dispatch(dailyUpdateByLoginIdAsQuarter(loginId, token, format(data.startDate, "yyyy-MM-dd"), format(data.endDate, "yyyy-MM-dd")));
    }

    const loadDashBoard = (token, loginId) => {
        dispatch(getLeaveBalanceList(token, loginId, 'Apply'));
        dispatch(getAlerts(loginId, token));
        dispatch(getAllTaskDetailsByloginId("0", token));
        dispatch(getEmployeeData(token))
        dispatch(getTaskDataProjectWiseData(token))
        dispatch(getEmpTaskCountList(token))
        dispatch(empList(token, "SUP"));
        getDailyUpdateData(role);
    }


    useEffect(() => {
        if (loginId && token) {
            loadDashBoard(token, loginId);
        }
    }, [token, loginId]);

    const supportEmpOptions = useMemo(
        () => [
            { value: 0, label: "ALL" }, // Static option
            ...supportlists.map((emp) => ({
                value: emp.loginId,
                label: `${emp.firstName} ${emp.lastName}`,
            })),
        ],
        [supportlists]
    );

    useEffect(() => {
        if (supportEmpOptions.length > 0 && selectedEmployee === null) {
            setSelectedEmployee(supportEmpOptions[0]);
        }
    }, [supportEmpOptions, selectedEmployee]);

    useEffect(() => {
        if (allTaskDetails?.taskMaster?.length > 0) {
            const counts = allTaskDetails.taskMaster.reduce((acc, task) => {
                const status = task.status || "Unknown";
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});
            setStatusCounts(counts);
        } else {
            setStatusCounts({});
        }
    }, [allTaskDetails]);


    const statutoryList = alerts?.alertDto?.filter(item => item.type === 'S') || [];
    const dailyList = alerts?.alertDto?.filter(item => item.type === 'D') || [];


    const STATUS_ORDER = [
        "To Do",
        "In Progress",
        "In Testing",
        "Rejected",
        "Reopened",
        "Completed",
        "On Hold",
        "Unknown",
    ];

    const STATUS_COLOR_MAP = {
        "To Do": "#42a5f5",
        "In Progress": "#aebb66ff",
        "In Testing": "#ffca28",
        "Rejected": "#ef5350",
        "Reopened": "#ab47bc",
        "Completed": "#2a962fff",
        "On Hold": "#b5da72",
        "Unknown": "#8d6e63",
    };

    //  Build labels, values, and colors arrays in the fixed order
    const labels = [];
    const dataValues = [];
    const colors = [];

    STATUS_ORDER.forEach((statusKey) => {
        const count = statusCounts[statusKey] || 0;
        if (count > 0) {
            labels.push(statusKey);
            dataValues.push(count);
            colors.push(STATUS_COLOR_MAP[statusKey] || "#8d6e63");
        }
    });

    //  Prepare your chart dataset using those arrays
    const taksDataforChart = {
        labels,
        datasets: [
            {
                label: "Task Status Count",
                data: dataValues,
                backgroundColor: colors,
                borderColor: "#ffffff",
                borderWidth: 2,
            },
        ],
    };
    const dashOptionsforChart = {
        responsive: true,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    color: "#333",
                    boxWidth: 10, // default is 40 → make smaller
                    boxHeight: 10,
                    padding: 12,

                    font: { size: 13, weight: "500" },
                },
            },
            datalabels: {
                color: "#fff",
                font: { weight: "bold", size: 14 },
                formatter: (value) => value,
            },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.label}: ${context.formattedValue}`,
                },
            },
        },
    };

    const totalTasks = allTaskDetails?.taskMaster?.length;

    // Starting from here*************************************

    const designationEntries = employeeData?.countByDesignationName
        ? Object.entries(employeeData.countByDesignationName)
            .filter(([key]) => key !== "HR" && key !== "Super Admin")
        : [];

    const empLabels = designationEntries.map(([designation]) => designation || "N/A");
    const empCounts = designationEntries.map(([, count]) => Number(count) || 0);

    const totalEmployees = designationEntries.reduce(
        (sum, [, count]) => sum + (count ?? 0),
        0
    );

    const EMP_COLORS = [
        "#4CAF50",
        "#2196F3",
        "#FF9800",
        "#9C27B0",
        "#F44336",
        "#00BCD4",
        "#795548"
    ];

    const employeePieData = {
        labels: [empLabels],
        datasets: [
            {
                data: empCounts,
                backgroundColor: empCounts.map(
                    (_, index) => EMP_COLORS[index % EMP_COLORS.length]
                ),
                borderColor: "#fff",
                borderWidth: 2
            }
        ]
    };

    const employeePieOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || "";
                        const value = Number(context.raw) || 0;

                        const total = context.dataset.data.reduce(
                            (sum, v) => sum + (Number(v) || 0),
                            0
                        );

                        const percentage =
                            total > 0
                                ? ((value / total) * 100).toFixed(1)
                                : "0.0";

                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                color: "#fff",
                font: {
                    size: 13,
                    weight: "bold"
                },
                formatter: (value) => (value === 0 ? "" : value)
            }
        }
    };

    const hasEmployeeData = empCounts.some(v => v > 0);


    const projects = Array.isArray(dailyUpdatebyIdPerQuarter?.projects)
        ? dailyUpdatebyIdPerQuarter.projects
        : [];

    const totalCount = projects.reduce(
        (sum, p) => sum + (Number(p?.count) || 0),
        0
    );

    const COLORS = [
        "#4CAF50",
        "#2196F3",
        "#FF9800",
        "#9C27B0",
        "#F44336",
        "#00BCD4"
    ];

    const labels1 = projects.map(p => p?.projectCode ?? "N/A");
    const counts = projects.map(p => Number(p?.count) || 0);

    const data = {
        labels1,
        datasets: [
            {
                label: "Project Count",
                data: counts,
                backgroundColor: counts.map(
                    (_, index) => COLORS[index % COLORS.length]
                ),
                borderColor: "#fff",
                borderWidth: 2
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            },

            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label ?? "";
                        const value = Number(context.raw) || 0;

                        const dataset = Array.isArray(context.dataset?.data)
                            ? context.dataset.data
                            : [];

                        const total = dataset.reduce(
                            (sum, v) => sum + (Number(v) || 0),
                            0
                        );

                        const percentage =
                            total > 0
                                ? ((value / total) * 100).toFixed(1)
                                : "0.0";

                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            },

            datalabels: {
                color: "#fff",
                font: {
                    size: 13,
                    weight: "bold"
                },
                formatter: (value) => (Number(value) === 0 ? "" : value)
            }
        }
    };




    const inTimeBuckets = Array.isArray(dailyUpdatebyIdPerQuarter?.inTimeBuckets)
        ? dailyUpdatebyIdPerQuarter.inTimeBuckets
        : [];

    const outTimeBuckets = Array.isArray(dailyUpdatebyIdPerQuarter?.outTimeBuckets)
        ? dailyUpdatebyIdPerQuarter.outTimeBuckets
        : [];

    // helper to get count by label (SAFE)
    const getCount = (list, label) => {
        if (!Array.isArray(list)) return 0;

        const item = list.find(i => i?.label === label);
        return Number(item?.count) || 0;
    };

    const pieData = [
        { label: "08 AM - 10 AM", count: getCount(inTimeBuckets, "08 AM - 10 AM") },
        { label: "10 AM - 12 PM", count: getCount(inTimeBuckets, "10 AM - 12 PM") },
        { label: "02 PM - 05 PM", count: getCount(outTimeBuckets, "14 PM - 17 PM") },
        { label: "05 PM - 08 PM", count: getCount(outTimeBuckets, "17 PM - 20 PM") }
    ];

    const TIME_COLORS = [
        "#4CAF50", // In (early)
        "#2196F3", // In (late)
        "#FF9800", // Out (early)
        "#bf48ddff"  // Out (late)
    ];

    // normalize counts (extra safety)
    const counts1 = pieData.map(p => Number(p?.count) || 0);

    const timeData = {
        labels: pieData.map(p => p.label ?? ""),
        datasets: [
            {
                data: counts1,
                backgroundColor: TIME_COLORS,
                borderColor: "#fff",
                borderWidth: 2
            }
        ]
    };

    const timeOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            },

            datalabels: {
                color: "#fff",
                font: {
                    size: 13,
                    weight: "bold"
                },
                formatter: (value) => (Number(value) === 0 ? "" : value)
            },

            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = Number(context.raw) || 0;
                        return `${value}`;
                    }
                }
            }
        }
    };

    // Endining from here*************************************



    const handleCompletd = async (item) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            html: `Do you want to complete the work?<br><strong>"${item.subject}"</strong>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, submit it!",
            cancelButtonText: "No, cancel",
        })

        if (result.isConfirmed) {
            dispatch(alertCompleted(token, item.alertId))
                .then((response) => {
                    if (response.statusMessage === "SUCCESS") {
                        dispatch(getAlerts(loginId, token));
                        Swal.fire({
                            title: "Success!",
                            text: response.statusDesc,
                            icon: "success",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                    } else {
                        Swal.fire({
                            title: "Error!",
                            text: response.statusDesc,
                            icon: "error",
                            confirmButtonText: "OK",
                        });
                    }
                })
                .catch(() => {
                    Swal.fire({
                        title: "Error!",
                        text: "There was an error updating the alert",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                })
        }
    }

    const isDatePast = (dateStr) => {
        if (!dateStr) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const [day, month, year] = dateStr.split("-");
        const endDate = new Date(year, month - 1, day);
        endDate.setHours(0, 0, 0, 0);
        return endDate < today;
    };

    const navigateOptions = {
        "Office": "/employee",
        "Support": "/supportEmployee",
        "Contract": "/contractEmployee"
    }

    const today = stripTime(new Date());
    const fiveDaysAhead = new Date(today);
    fiveDaysAhead.setDate(today.getDate() + 3);

    const filteredStatutoryList = statutoryList.filter(s => {
        if (!s.date) return false; // skip missing dates
        const backendDate = parseDDMMYYYY(s.date.trim());
        if (!(backendDate instanceof Date) || isNaN(backendDate)) return false;

        const backendDateNoTime = stripTime(backendDate);
        return backendDateNoTime <= today || (backendDateNoTime >= today && backendDateNoTime <= fiveDaysAhead);
    });

    const handleEmpChange = (selectedOption) => {
        setSelectedEmployee(selectedOption);
        if (selectedOption?.value === 0) {
            getDailyUpdateData(role);
        } else {
            getDailyUpdateData(selectedOption?.value);
        }

    };



    return (
        <div>
            <div className="row">
                {/* CARD 1 */}
                <div className="col-md-4 mb-4 d-flex">
                    <div className="card shadow-sm rounded-4 h-100 w-100 admin-dashboard-card">
                        <div className="card-body d-flex flex-column align-items-center">

                            <h6 className="text-center mb-3 fw-bold">
                                Employee Data
                            </h6>

                            {/* CHART AREA (only this grows) */}
                            <div
                                className="flex-grow-1 d-flex justify-content-center align-items-center w-100"
                                style={{ maxHeight: 260 }}
                            >
                                {hasEmployeeData ? (
                                    <Pie data={employeePieData} options={employeePieOptions} />
                                ) : (
                                    <p>No data available</p>
                                )}
                            </div>

                            {/* LEGEND (does NOT grow card height) */}
                            <div
                                className="mt-auto"
                                style={{
                                    marginTop: 12,
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "16px",
                                    flexWrap: "wrap"
                                }}
                            >
                                {designationEntries.map(([designation, count], index) => (
                                    <div
                                        key={designation}
                                        onClick={() => navigate(navigateOptions[designation])}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor:
                                                    EMP_COLORS[index % EMP_COLORS.length],
                                                borderRadius: 2
                                            }}
                                        />
                                        <span style={{ fontSize: 12 }}>
                                            {designation} ({count ?? 0})
                                        </span>
                                    </div>
                                ))}
                                {totalEmployees > 0 && (
                                    <div
                                        onClick={() => navigate("/allEmployees")}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: "#9e38e1",
                                                borderRadius: 2
                                            }}
                                        />
                                        <span style={{ fontSize: 12 }}>
                                            Total ({totalEmployees})
                                        </span>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>



                {/* CARD 2 */}
                <div className="col-md-4 mb-4 d-flex">
                    <div className="card shadow-sm rounded-4 h-100 w-100 admin-dashboard-card">
                        <div className="card-body position-relative d-flex flex-column align-items-center">

                            {/* HEADER */}
                            <div className="d-flex align-items-center justify-content-between mb-3 w-100">
                                <h6 className="fw-bold mb-0">
                                    {`In/Out Time Overview Daily Update (${quarterInfo?.quarter})`}
                                </h6>

                                <Select
                                    options={supportEmpOptions}
                                    value={selectedEmployee}
                                    onChange={handleEmpChange}
                                    placeholder="Employee"
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: 200
                                        }),
                                        menu: (base) => ({ ...base, textAlign: "left" }),
                                        option: (base) => ({ ...base, textAlign: "left" })
                                    }}
                                />
                            </div>

                            {/* CHART AREA (ONLY THIS GROWS) */}
                            <div
                                className="flex-grow-1 d-flex justify-content-center align-items-center w-100"
                                style={{ maxHeight: 260 }}
                            >
                                {pieData.some(p => p.count > 0) ? (
                                    <Pie data={timeData} options={timeOptions} />
                                ) : (
                                    <p>No data available</p>
                                )}
                            </div>

                            {/* LEGEND / NOTE (LOCKED TO BOTTOM) */}
                            <div
                                className="mt-auto w-100"
                                style={{
                                    marginTop: 12,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    columnGap: "18px",
                                    flexWrap: "wrap"
                                }}
                            >
                                {/* IN TIME */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>In Time</span>

                                    {pieData.slice(0, 2).map((p, idx) => (
                                        <div
                                            key={idx}
                                            style={{ display: "flex", alignItems: "center", gap: "6px" }}
                                        >
                                            <div
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: TIME_COLORS[idx],
                                                    borderRadius: 2
                                                }}
                                            />
                                            <span style={{ fontSize: 12, color: "#555" }}>
                                                {p.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* DIVIDER */}
                                <div
                                    style={{
                                        width: 1,
                                        height: 16,
                                        backgroundColor: "#ccc"
                                    }}
                                />

                                {/* OUT TIME */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>Out Time</span>

                                    {pieData.slice(2, 4).map((p, idx) => (
                                        <div
                                            key={idx}
                                            style={{ display: "flex", alignItems: "center", gap: "6px" }}
                                        >
                                            <div
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: TIME_COLORS[idx + 2],
                                                    borderRadius: 2
                                                }}
                                            />
                                            <span style={{ fontSize: 12, color: "#555" }}>
                                                {p.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* NOTE */}
                                <div style={{ width: "100%", textAlign: "left", marginTop: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
                                        The average in-time delay after 9:00 AM is{" "}
                                        <strong>
                                            {dailyUpdatebyIdPerQuarter?.avgInTimeDelayMinutes} minutes
                                        </strong>.
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>


                {/* CARD 3 */}
                <div className="col-md-4 mb-4 d-flex">
                    <div className="card shadow-sm rounded-4 h-100 w-100 admin-dashboard-card">
                        <div className="card-body d-flex flex-column align-items-center">

                            {/* HEADER */}
                            <h6 className="text-center mb-3 w-100 fw-bold">
                                {`Issue Overview (${quarterInfo?.quarter})`}
                            </h6>

                            {/* CHART AREA (ONLY THIS GROWS) */}
                            <div
                                className="flex-grow-1 d-flex justify-content-center align-items-center w-100"
                                style={{ maxHeight: 260 }}
                            >
                                {data.labels1.length > 0 ? (
                                    <Pie data={data} options={options} />
                                ) : (
                                    <p>No data available</p>
                                )}
                            </div>

                            {/* LEGEND (LOCKED TO BOTTOM) */}
                            <div
                                className="mt-auto"
                                style={{
                                    marginTop: 8,
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "9px",
                                    flexWrap: "wrap"
                                }}

                            >
                                {data.labels1.length > 0 &&
                                    projects.map((p, index) => (
                                        <div
                                            key={p.projectCode}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: COLORS[index % COLORS.length],
                                                    borderRadius: 2
                                                }}
                                            />
                                            <span style={{ fontSize: 11 }}>
                                                {p.projectCode} ({p.count})
                                            </span>
                                        </div>

                                    ))
                                }
                                {data.labels1.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: "#9e38e1",
                                                borderRadius: 2
                                            }}
                                        />
                                        <span style={{ fontSize: 11 }}>
                                            Total ({totalCount})
                                        </span>
                                    </div>
                                )}
                            </div>


                        </div>
                    </div>
                </div>

            </div>




            <div className='row'>
                <div className="col-md-8 mb-4">
                    <div className="row">
                        <div className="col-md-12 mb-4">
                            <div className="card shadow-sm rounded-4" style={{ minHeight: "409px" }}>
                                <div className="card-body">

                                    <div className="row">
                                        {/* LEFT: Chart */}
                                        <div className="col-md-3 d-flex flex-column justify-content-center align-items-center">

                                            <h6 className="text-center mb-3 w-100 fw-bold">Task Status Overview</h6>

                                            <div
                                                className="mt-auto"
                                                style={{
                                                    width: "111%",
                                                    height: 280,
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: "9px",
                                                    marginTop: 8,
                                                    flexWrap: "wrap"
                                                }}
                                            >
                                                {labels.length > 0 ? (
                                                    <Pie data={taksDataforChart} options={dashOptionsforChart} />
                                                ) : (
                                                    <p className="text-center text-muted">No data available</p>
                                                )}
                                                {labels.length > 0 && (
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "4px",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                backgroundColor: "#9e38e1",
                                                                borderRadius: 2
                                                            }}
                                                        />
                                                        <span style={{ fontSize: 13 }}>
                                                            Total ({totalTasks})
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                        </div>

                                        {/* RIGHT: Task List */}
                                        <div className="col-md-9">

                                            <h6 className="text-center mb-1 fw-bold">Task Details</h6>

                                            <div
                                                className="task-container"
                                                style={{
                                                    flex: 1,
                                                    overflowY: "auto",
                                                    maxHeight: "300px",
                                                }}
                                            >

                                                {/* ---------------- PC TABLE VIEW (Your exact same code) --------------- */}
                                                <div className="d-none d-md-block">
                                                    {allTaskDetails?.taskMaster && allTaskDetails.taskMaster.length > 0 ? (
                                                        <table className="table table-sm table-hover align-middle mb-0">
                                                            <thead className="table-light sticky-header">
                                                                <tr>
                                                                    <th>Task No</th>
                                                                    <th>Employee</th>
                                                                    <th>Title</th>
                                                                    <th>PDC</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {allTaskDetails.taskMaster
                                                                    .filter(task => task.status !== "Completed")
                                                                    .map((task, index) => (
                                                                        <tr key={task.id || index} className="fade-in-row"
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={() => navigate('/taskDetails')}>
                                                                            <td
                                                                                style={{
                                                                                    fontWeight: '500',
                                                                                    color: STATUS_COLOR_MAP[task.status] || STATUS_COLOR_MAP["Unknown"],
                                                                                }}
                                                                            >
                                                                                {task.taskNo || '-'}
                                                                            </td>

                                                                            <td>{task.assignName || '-'}</td>

                                                                            <td className="text-start">{task.title || '-'}</td>

                                                                            <td
                                                                                className={`fw-medium ${isDatePast(task.endDate) ? 'text-danger' : ''}`}
                                                                            >
                                                                                {task.endDate || '-'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <p className="text-center text-muted mt-3">No tasks available</p>
                                                    )}
                                                </div>

                                                {/* ---------------- MOBILE CARD VIEW (ADDED) ---------------- */}
                                                <div className="d-block d-md-none">
                                                    {allTaskDetails?.taskMaster && allTaskDetails.taskMaster.length > 0 ? (
                                                        allTaskDetails.taskMaster
                                                            .filter(task => task.status !== "Completed")
                                                            .map((task, index) => (
                                                                <div key={task.id || index} className="task-card shadow-sm fade-slide-up">

                                                                    <div className="task-card-header">
                                                                        <span
                                                                            className="task-status"
                                                                            style={{ background: STATUS_COLOR_MAP[task.status] }}
                                                                        >
                                                                            {task.status}
                                                                        </span>

                                                                        <span className="task-number">{task.taskNo || '-'}</span>
                                                                    </div>

                                                                    <div className="task-card-body">
                                                                        <p><strong>Employee:</strong> {task.assignName || '-'}</p>
                                                                        <p><strong>Title:</strong> {task.title || '-'}</p>
                                                                        <p className={isDatePast(task.endDate) ? "text-danger fw-bold" : ""}>
                                                                            <strong>PDC:</strong> {task.endDate || '-'}
                                                                        </p>
                                                                    </div>

                                                                </div>
                                                            ))
                                                    ) : (
                                                        <p className="text-center text-muted mt-3">No tasks available</p>
                                                    )}
                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className='col-md-4'>
                    <div className="row">
                        <div className="col-md-6">
                            <div
                                className="card shadow-sm h-100 rounded-4"
                            >
                                <div
                                    className="card-header text-center fw-semibold text-dark rounded-top-4"
                                    style={{
                                        background: "linear-gradient(90deg, rgb(201 122 227), rgb(155 227 249))",
                                        fontSize: "1.1rem",
                                        padding: "10px 0",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    <i className="bi bi-file-earmark-text me-2 text-info"></i>
                                    Statutory Alert
                                </div>

                                <div className="card-body p-3" style={{ minHeight: "355px" }}>
                                    {filteredStatutoryList && filteredStatutoryList.length > 0 ? (
                                        <ul
                                            className="list-group list-group-flush custom-scroll"
                                            style={{ maxHeight: "327px", overflowY: "auto" }}
                                        >
                                            {filteredStatutoryList
                                                .map((item, index) => (
                                                    <li
                                                        key={index}
                                                        className="list-group-item d-flex justify-content-between align-items-center border-0 border-bottom"
                                                        style={{
                                                            fontSize: "14px",
                                                            backgroundColor: "transparent",
                                                            transition: "all 0.3s ease",
                                                        }}
                                                        onMouseEnter={(e) =>
                                                            (e.currentTarget.style.backgroundColor = "#d7e0fdff")
                                                        }
                                                        onMouseLeave={(e) =>
                                                            (e.currentTarget.style.backgroundColor = "transparent")
                                                        }
                                                    >
                                                        {/* Left side - text info */}
                                                        <div className="d-flex flex-column text-start">
                                                            <span className="fw-medium text-secondary">
                                                                {item?.subject ?? "-"}
                                                            </span>
                                                            <small className="text-muted">
                                                                <i className="bi bi-calendar-event me-1"></i>
                                                                {item?.date ?? "-"}
                                                            </small>
                                                        </div>

                                                        {/* Right side - button */}
                                                        {(() => {
                                                            const [day, month, year] = item?.date?.split("-");
                                                            const itemDate = new Date(`${year}-${month}-${day}`);
                                                            const today = new Date();

                                                            // Normalize both (ignore time)
                                                            itemDate.setHours(0, 0, 0, 0);
                                                            today.setHours(0, 0, 0, 0);

                                                            // Calculate 3 days before the item date
                                                            const threeDaysBefore = new Date(itemDate);
                                                            threeDaysBefore.setDate(itemDate.getDate() - 3);

                                                            // Button visible if:
                                                            // - Today >= 3 days before item date (window opens)
                                                            // - AND (task not completed yet)
                                                            const isWithinWindow = today >= threeDaysBefore || today > itemDate;

                                                            return (
                                                                isWithinWindow && (
                                                                    <button
                                                                        className="btn btn-primary btn-sm rounded-5 smallButton"
                                                                        // style={{ height: "25px", fontSize: "11px" }}
                                                                        onClick={() => handleCompletd(item)}
                                                                    >
                                                                        <FaCheck />
                                                                    </button>
                                                                )
                                                            );
                                                        })()}

                                                    </li>
                                                ))}
                                        </ul>

                                    ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: "100%" }}>
                                            <i className="bi bi-inbox fs-2 mb-2 text-secondary"></i>
                                            <span>No data available</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6 cs-card-height">
                            <div
                                className="card shadow-sm h-100 rounded-4"
                            >
                                <div
                                    className="card-header text-center fw-semibold text-dark rounded-top-4"
                                    style={{
                                        background: "linear-gradient(90deg, rgb(184 241 148), rgb(129 210 249))",
                                        fontSize: "1.1rem",
                                        padding: "10px 0",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    <i className="bi bi-file-earmark-text me-2 text-info"></i>
                                    Daily Alert
                                </div>

                                <div className="card-body p-3" style={{ minHeight: "355px" }}>
                                    {dailyList && dailyList.length > 0 ? (
                                        <ul
                                            className="list-group list-group-flush custom-scroll"
                                            style={{ maxHeight: "327px", overflowY: "auto" }}
                                        >
                                            {dailyList.map((item, index) => (
                                                <li
                                                    key={index}
                                                    className="list-group-item d-flex justify-content-between align-items-center border-0 border-bottom"
                                                    style={{
                                                        fontSize: "14px",
                                                        backgroundColor: "transparent",
                                                        transition: "all 0.3s ease",
                                                    }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.backgroundColor = "#d7fdeaff")
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.backgroundColor = "transparent")
                                                    }
                                                >
                                                    {/* Left side - text info */}
                                                    <div className="d-flex flex-column text-start">
                                                        <span className="fw-medium text-secondary">{item?.subject ?? "-"}</span>
                                                        <small className="text-muted">
                                                            <i className="bi bi-calendar-event me-1"></i>
                                                            {item?.date ?? "-"}
                                                        </small>
                                                    </div>

                                                    {/* Right side - button */}
                                                    {(() => {
                                                        const [day, month, year] = item?.date?.split("-");
                                                        const itemDate = new Date(`${year}-${month}-${day}`);
                                                        const today = new Date();

                                                        // Normalize both (ignore time)
                                                        itemDate.setHours(0, 0, 0, 0);
                                                        today.setHours(0, 0, 0, 0);

                                                        // Calculate 3 days before the item date
                                                        const threeDaysBefore = new Date(itemDate);
                                                        threeDaysBefore.setDate(itemDate.getDate() - 3);

                                                        // Button visible if:
                                                        // - Today >= 3 days before item date (window opens)
                                                        // - AND (task not completed yet)
                                                        const isWithinWindow = today >= threeDaysBefore || today > itemDate;

                                                        return (
                                                            isWithinWindow && (
                                                                <button
                                                                    className="btn btn-primary btn-sm rounded-5 smallButton"
                                                                    // style={{ height: "25px", fontSize: "11px" }}
                                                                    onClick={() => handleCompletd(item)}
                                                                >
                                                                    <FaCheck />
                                                                </button>

                                                            )
                                                        );
                                                    })()}

                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: "100%" }}>
                                            <i className="bi bi-inbox fs-2 mb-2 text-secondary"></i>
                                            <span>No data available</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminDash;